import { useState } from "react"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { Menu } from "lucide-react"
import { api } from "../../convex/_generated/api"
import { toast } from "sonner"
import { authClient, roleHome, signOutSucceeded } from "@/lib/auth-client"
import {
  activeMarketingNav,
  type MarketingNavKey,
} from "@/lib/marketing-chrome"
import { Logo } from "@/components/logo"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const NAV_LINKS: {
  key: MarketingNavKey
  to: "/jobs" | "/recruit" | "/jobs/browse" | "/hackathons"
  label: string
}[] = [
  { key: "candidates", to: "/jobs", label: "For candidates" },
  { key: "companies", to: "/recruit", label: "For companies" },
  { key: "browse", to: "/jobs/browse", label: "Browse jobs" },
  { key: "hackathons", to: "/hackathons", label: "Hackathons" },
]

function linkClass(active: boolean) {
  return active ? "mkt-nav-link mkt-nav-link--active" : "mkt-nav-link"
}

export function MarketingHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const profile = useQuery(api.profiles.me)
  const { data: session } = authClient.useSession()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const loggedIn = !!session?.user
  const active = activeMarketingNav(pathname)

  const actions = loggedIn ? (
    <>
      {profile?.role ? (
        <Link
          to={roleHome(profile.role)}
          className="home-wf-btn home-wf-btn--ink mkt-nav-join"
        >
          My desk
        </Link>
      ) : profile === null ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="home-wf-btn home-wf-btn--ink mkt-nav-join">
            Finish signup
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="mkt-nav-dropdown">
            <DropdownMenuItem asChild>
              <Link to="/jobs/join" onClick={() => setOpen(false)}>
                Go to Candidate
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/recruit/join" onClick={() => setOpen(false)}>
                Go to Recruiter
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <button
        type="button"
        className="mkt-nav-text"
        onClick={async () => {
          if (!(await signOutSucceeded())) {
            toast.error("Could not sign out. Please try again.")
            return
          }
          setOpen(false)
          void navigate({ to: "/" })
        }}
      >
        Sign out
      </button>
    </>
  ) : (
    <>
      <Link
        to="/login"
        className="home-wf-btn home-wf-btn--outline mkt-nav-join"
        onClick={() => setOpen(false)}
      >
        Log in
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger className="home-wf-btn home-wf-btn--ink mkt-nav-join">
          Join
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="mkt-nav-dropdown">
          <DropdownMenuItem asChild>
            <Link to="/jobs/join" onClick={() => setOpen(false)}>
              Candidate join
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/recruit/join" onClick={() => setOpen(false)}>
              Company join
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )

  return (
    <header className="mkt-nav home-wf">
      <div className="home-wf-shell mkt-nav-inner">
        <Link to="/" className="home-wf-brand" aria-label="Hire home">
          <Logo />
        </Link>

        <nav className="mkt-nav-links" aria-label="Primary">
          {NAV_LINKS.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={linkClass(active === item.key)}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="mkt-nav-actions mkt-nav-actions--desktop">{actions}</div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="mkt-nav-menu"
              aria-label="Open menu"
            >
              <Menu className="size-5" aria-hidden />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="mkt-nav-sheet home-wf w-[min(100%,20rem)]">
            <SheetHeader>
              <SheetTitle className="sr-only">Menu</SheetTitle>
            </SheetHeader>
            <nav className="mkt-nav-drawer" aria-label="Mobile primary">
              {NAV_LINKS.map((item) => (
                <SheetClose asChild key={item.key}>
                  <Link
                    to={item.to}
                    className={linkClass(active === item.key)}
                  >
                    {item.label}
                  </Link>
                </SheetClose>
              ))}
              <div className="mkt-nav-drawer-actions">{actions}</div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
